import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiUsers, 
  FiZap, 
  FiShield, 
  FiSettings,
  FiArrowRight,
  FiBookOpen,
  FiPlay
} from 'react-icons/fi';

const Guide = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const guideCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: FiPlay,
      description: 'Learn the basics of using Bervin',
      articles: [
        { title: 'Quick Start Guide', path: '/guide/quick-start' },
        { title: 'First AI Conversation', path: '/guide/first-conversation' }
      ]
    },
    {
      id: 'team-features',
      title: 'Team Features',
      icon: FiUsers,
      description: 'Collaborate with your team using AI',
      articles: [
        { title: 'Team Chat with AI', path: '/guide/team-chat', featured: true }
      ]
    },
    {
      id: 'ai-models',
      title: 'AI Models & Features',
      icon: FiZap,
      description: 'Understanding different AI capabilities',
      articles: [
        { title: 'Available AI Models', path: '/guide/ai-models' },
        { title: 'Multi-AI Conversations', path: '/guide/multi-ai' }
      ]
    },
    {
      id: 'mcp-integration',
      title: 'MCP Integration',
      icon: FiSettings,
      description: 'Extend AI capabilities with MCP servers',
      articles: [
        { title: 'MCP Server Setup', path: '/guide/mcp-setup' }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-8"
            >
              <FiBookOpen className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              <span className="gradient-text">Bervin Guide</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Master the art of AI collaboration with comprehensive guides, tutorials, and best practices 
              for getting the most out of Bervin's powerful features.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Article Banner */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-3xl blur-xl"></div>
            <div className="relative bg-gradient-to-r from-dark-800/80 to-secondary-900/80 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12">
              <div className="flex items-center mb-4">
                <div className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full text-sm font-semibold text-white mr-4">
                  🔥 Featured
                </div>
                <span className="text-gray-400">New Feature Guide</span>
              </div>
              
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 gradient-text">
                Team Chat with AI - Real-time Collaboration
              </h2>
              
              <p className="text-gray-300 text-lg mb-8 max-w-4xl">
                Discover how to collaborate with your team using AI in real-time. Chat with AI models while 
                your team members can see the conversation live, share insights, and build on ideas together.
              </p>
              
              <Link
                to="/guide/team-chat"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Explore Team Chat
                <FiArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Guide Categories */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 gradient-text">
              Explore All Guides
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Browse our comprehensive collection of guides organized by category
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {guideCategories.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-secondary-500/50 transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <div className="p-3 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl mr-4">
                      <category.icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-white">
                      {category.title}
                    </h3>
                  </div>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {category.description}
                  </p>
                  
                  <div className="space-y-3">
                    {category.articles.map((article, articleIndex) => (
                      <Link
                        key={articleIndex}
                        to={article.path}
                        className="flex items-center justify-between p-3 rounded-lg bg-dark-700/30 hover:bg-dark-700/60 transition-all duration-200 group/article"
                      >
                        <div className="flex items-center">
                          {article.featured && (
                            <div className="w-2 h-2 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mr-3"></div>
                          )}
                          <span className="text-gray-300 group-hover/article:text-white transition-colors duration-200">
                            {article.title}
                          </span>
                        </div>
                        <FiArrowRight className="w-4 h-4 text-gray-500 group-hover/article:text-secondary-400 transition-colors duration-200" />
                      </Link>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-gradient-to-r from-dark-800/50 to-secondary-900/50 backdrop-blur-sm border border-white/10 rounded-3xl p-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 gradient-text">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Jump into Bervin and start collaborating with AI today. 
              Our guides will help you every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a
                href="https://bervin.appiworks.com/app"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                Try Bervin Now
              </a>
              <Link
                to="/contact"
                className="px-8 py-4 border border-white/20 rounded-xl font-semibold text-white text-lg hover:bg-white/10 transition-all duration-300"
              >
                Need Help?
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Guide;
